
import React, { useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';

interface ExhibitDetailsProps {
  description: string;
  location: {
    room: string;
    floor: number;
  } | null;
  hasVideo: boolean;
  videoUrl?: string;
  language: string;
}

const ExhibitDetails = ({ description, location, hasVideo, videoUrl, language }: ExhibitDetailsProps) => {
  const { t } = useLanguage();
  
  // Set the correct text direction based on language
  const textDir = ['he', 'ar'].includes(language) ? 'rtl' : 'ltr';
  const isRTL = textDir === 'rtl';
  
  // EXTREMELY strict validation - never show dummy/placeholder data
  const hasValidLocation = location && 
    typeof location === 'object' && 
    location.room && 
    typeof location.room === 'string' &&
    location.room.trim() !== '' && 
    // Exclude any dummy/placeholder data patterns
    !location.room.toLowerCase().includes('room 1') &&
    !location.room.toLowerCase().includes('room1') &&
    !location.room.toLowerCase().match(/^room\s*\d+$/i) &&
    location.room !== 'Room 1' && 
    location.room !== 'room 1' &&
    location.room !== 'Room1' &&
    location.room !== 'room1' &&
    typeof location.floor === 'number' &&
    location.floor > 0 &&
    // Don't show floor 1 if it might be dummy data with generic room names
    !(location.floor === 1 && location.room.toLowerCase().includes('room'));
  
  console.log('ExhibitDetails location validation:', {
    rawLocation: location,
    roomValue: location?.room,
    floorValue: location?.floor,
    hasValidLocation: hasValidLocation,
    roomMatchesDummyPattern: location?.room ? location.room.toLowerCase().match(/^room\s*\d+$/i) : false
  });
  
  return (
    <div className="mb-6">
      <div className="p-6 bg-white/80 backdrop-blur-sm rounded-xl border border-museum-light">
        <div className="text-gray-700 space-y-4">
          {/* Location information - only show if valid location data exists and it's not dummy data */}
          {hasValidLocation && (
            <div className="text-sm" dir={textDir}>
              <p>
                {language === 'he' ? 'מיקום:' : language === 'ar' ? 'الموقع:' : 'Location:'} 
                {location.room}, 
                {language === 'he' ? ' קומה ' : language === 'ar' ? ' طابق ' : ' Floor '} 
                {location.floor}
              </p>
            </div>
          )}
          
          {/* Description - with proper text direction */}
          {description ? (
            <div 
              dir={textDir} 
              className="whitespace-pre-wrap text-base" 
              lang={language}
              style={{ 
                textAlign: isRTL ? 'right' : 'left',
                unicodeBidi: 'embed',
                direction: textDir
              }}
            >
              <p>{description}</p>
            </div>
          ) : (
            <div className="text-sm text-gray-400" dir={textDir}>
              {language === 'he' ? 'אין תיאור זמין' : language === 'ar' ? 'לا יوجد وصف متاح' : 'No description available'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExhibitDetails;
